/**
* @file 旅游包车 - 数据统计看板
* @description 对标 analysisStatistics.vue 布局：时间筛选 + 概览卡片 + 图表网格 + 排行表格
* */
<template>
  <div id="charteredStatistics">
    <!-- 顶部筛选条件 -->
    <div class="filter-bar">
      <div class="filter-left">
        <el-radio-group v-model="timeType" size="small" @change="onTimeTypeChange">
          <el-radio-button :label="0">今日</el-radio-button>
          <el-radio-button :label="1">昨日</el-radio-button>
          <el-radio-button :label="2">近7天</el-radio-button>
          <el-radio-button :label="3">近30天</el-radio-button>
          <el-radio-button :label="4">近半年</el-radio-button>
          <el-radio-button :label="5">近一年</el-radio-button>
        </el-radio-group>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="自定义起"
          end-placeholder="自定义止"
          value-format="timestamp"
          size="small"
          style="margin-left: 12px; width: 280px;"
          @change="onDateRangeChange"
        ></el-date-picker>
      </div>
      <div class="filter-right">
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="refreshAll" :loading="loading">刷新数据</el-button>
      </div>
    </div>

    <!-- 经营概览卡片 -->
    <div class="section-title"><i class="el-icon-data-analysis"></i> 经营概览</div>
    <div class="overview-cards">
      <div class="ov-card" v-for="item in overviewCards" :key="item.label">
        <template v-if="item.isRate">
          <el-progress type="circle" :width="52" :stroke-width="5" :percentage="Number(item.value) || 0" :color="item.rateColor" />
          <div class="ov-card-info">
            <div class="ov-card-label">{{ item.label }}</div>
          </div>
        </template>
        <template v-else>
          <div class="ov-card-icon" :style="{ background: item.color }">
            <i :class="item.icon"></i>
          </div>
          <div class="ov-card-info">
            <div class="ov-card-value">{{ item.value }}</div>
            <div class="ov-card-label">{{ item.label }}</div>
          </div>
        </template>
      </div>
    </div>

    <!-- 图表区域：3列布局 -->
    <div class="section-title"><i class="el-icon-pie-chart"></i> 分布分析</div>
    <div class="charts-grid">
      <!-- 订单状态分布 -->
      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-tickets"></i> 订单状态分布</span>
        </div>
        <div class="chart-body" ref="statusChart"></div>
      </div>

      <!-- 套餐类型分布 -->
      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-goods"></i> 套餐类型分布</span>
        </div>
        <div class="chart-body" ref="comboChart"></div>
      </div>

      <!-- 下单城市TOP10 -->
      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-location-outline"></i> 下单城市 TOP10</span>
        </div>
        <div class="chart-body" ref="cityChart"></div>
      </div>
    </div>

    <!-- 时段分析：2列布局 -->
    <div class="section-title"><i class="el-icon-time"></i> 趋势分析</div>
    <div class="charts-grid-half">
      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-date"></i> 每日订单趋势</span>
        </div>
        <div class="chart-body" ref="trendChart"></div>
      </div>
      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-s-finance"></i> 每日收入趋势(元)</span>
        </div>
        <div class="chart-body" ref="revenueTrendChart"></div>
      </div>
    </div>

    <!-- 收入汇总表格 -->
    <div class="table-card" style="margin-bottom: 20px;">
      <div class="section-title"><i class="el-icon-s-data"></i> 收入汇总</div>
      <el-table :data="incomeList" border stripe size="small" max-height="320" style="width:100%">
        <el-table-column prop="label" label="项目" width="180" />
        <el-table-column prop="value" label="金额(元)" width="150">
          <template slot-scope="{ row }"><span class="util-money">¥{{ row.value }}</span></template>
        </el-table-column>
        <el-table-column prop="count" label="笔数" width="100" />
        <el-table-column prop="remark" label="说明" />
      </el-table>
    </div>

    <!-- 司机/车辆排行 -->
    <div class="tables-grid">
      <div class="table-card">
        <div class="section-title"><i class="el-icon-s-custom"></i> 司机收入排行 TOP20</div>
        <el-table :data="driverData" border stripe size="small" max-height="400" style="width:100%">
          <el-table-column type="index" label="序号" width="50" />
          <el-table-column prop="driverName" label="司机" min-width="80" />
          <el-table-column prop="driverPhone" label="电话" min-width="110" />
          <el-table-column prop="orderCount" label="订单数" min-width="70" sortable />
          <el-table-column prop="finishCount" label="完成" min-width="60" sortable />
          <el-table-column label="收入(元)" min-width="90" sortable prop="totalRevenue">
            <template slot-scope="{row}">{{ row.totalRevenue || '0.00' }}</template>
          </el-table-column>
          <el-table-column label="完成率" min-width="100" prop="completionRate">
            <template slot-scope="{row}">
              <el-progress v-if="row.completionRate != null" :percentage="Number(row.completionRate) || 0" :stroke-width="14" :text-inside="true" :color="row.completionRate >= 80 ? '#67C23A' : row.completionRate >= 50 ? '#E6A23C' : '#F56C6C'" />
              <span v-else>-</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div class="table-card">
        <div class="section-title"><i class="el-icon-truck"></i> 车辆收入排行 TOP20</div>
        <el-table :data="vehicleData" border stripe size="small" max-height="400" style="width:100%">
          <el-table-column type="index" label="序号" width="50" />
          <el-table-column prop="vehiclePlate" label="车辆号牌" min-width="100">
            <template slot-scope="{row}">
              <span v-if="row.vehiclePlate" :style="getPlateStyle(row.vehiclePlate)">{{ formatPlateNumber(row.vehiclePlate) }}</span>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="driverName" label="司机" min-width="80" />
          <el-table-column prop="orderCount" label="订单数" min-width="70" sortable />
          <el-table-column label="收入(元)" min-width="90" sortable prop="totalRevenue">
            <template slot-scope="{row}">{{ row.totalRevenue || '0.00' }}</template>
          </el-table-column>
          <el-table-column label="客单价" min-width="80" prop="avgOrderFee">
            <template slot-scope="{row}">{{ row.avgOrderFee || '0.00' }}</template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: "charteredStatistics",
  data() {
    return {
      loading: false,
      timeType: 0,
      dateRange: null,
      overview: {},
      incomeList: [],
      driverData: [],
      vehicleData: [],
      charts: {}
    };
  },
  computed: {
    overviewCards() {
      const o = this.overview;
      const total = o.totalOrders || 0;
      const completed = o.completedOrders || 0;
      const cancelled = o.cancelledOrders || 0;
      const completionRate = total > 0 ? parseFloat((completed * 100 / total).toFixed(1)) : 0;
      const cancelRate = total > 0 ? parseFloat((cancelled * 100 / total).toFixed(1)) : 0;
      return [
        { label: '总订单数', value: total, icon: 'el-icon-document', color: 'linear-gradient(135deg,#409EFF,#66B1FF)' },
        { label: '已完成', value: completed, icon: 'el-icon-circle-check', color: 'linear-gradient(135deg,#67C23A,#85CE61)' },
        { label: '待派车', value: o.pendingOrders || 0, icon: 'el-icon-truck', color: 'linear-gradient(135deg,#E6A23C,#F0B85A)' },
        { label: '进行中', value: o.ongoingOrders || 0, icon: 'el-icon-loading', color: 'linear-gradient(135deg,#409EFF,#66B1FF)' },
        { label: '已退票', value: cancelled, icon: 'el-icon-circle-close', color: 'linear-gradient(135deg,#F56C6C,#F89898)' },
        { label: '完成率', value: completionRate, icon: 'el-icon-pie-chart', color: 'linear-gradient(135deg,#67C23A,#85CE61)', isRate: true, rateColor: '#67C23A' },
        { label: '退票率', value: cancelRate, icon: 'el-icon-pie-chart', color: 'linear-gradient(135deg,#F56C6C,#F89898)', isRate: true, rateColor: '#F56C6C' },
        { label: '总收入(元)', value: o.totalRevenue != null ? Number(o.totalRevenue).toFixed(2) : '0.00', icon: 'el-icon-coin', color: 'linear-gradient(135deg,#E6A23C,#F0B85A)' },
        { label: '套餐费(元)', value: o.comboRevenue != null ? Number(o.comboRevenue).toFixed(2) : '0.00', icon: 'el-icon-goods', color: 'linear-gradient(135deg,#409EFF,#66B1FF)' },
        { label: '超额费(元)', value: o.beyondFeeRevenue != null ? Number(o.beyondFeeRevenue).toFixed(2) : '0.00', icon: 'el-icon-warning-outline', color: 'linear-gradient(135deg,#E6A23C,#F0B85A)' },
        { label: '均单价(元)', value: o.avgOrderFee != null ? Number(o.avgOrderFee).toFixed(2) : '0.00', icon: 'el-icon-price-tag', color: 'linear-gradient(135deg,#909399,#B0B3B8)' },
        { label: '司机提成(元)', value: o.driverCommission != null ? Number(o.driverCommission).toFixed(2) : '0.00', icon: 'el-icon-s-custom', color: 'linear-gradient(135deg,#909399,#B0B3B8)' },
        { label: '平台净收入(元)', value: o.platformIncome != null ? Number(o.platformIncome).toFixed(2) : '0.00', icon: 'el-icon-money', color: 'linear-gradient(135deg,#67C23A,#85CE61)' },
      ];
    }
  },
  mounted() {
    this.refreshAll();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    Object.values(this.charts).forEach(c => c && c.dispose());
  },
  methods: {
    handleResize() {
      Object.values(this.charts).forEach(c => c && c.resize());
    },
    onTimeTypeChange() {
      this.dateRange = null;
      this.refreshAll();
    },
    onDateRangeChange() {
      if (this.dateRange && this.dateRange.length === 2) {
        this.timeType = null;
        this.refreshAll();
      }
    },
    getParams() {
      if (this.dateRange && this.dateRange.length === 2) {
        return {
          startTime: Math.floor(this.dateRange[0] / 1000),
          endTime: Math.floor(this.dateRange[1] / 1000)
        };
      }
      return { timeType: this.timeType };
    },
    async refreshAll() {
      this.loading = true;
      const params = this.getParams();
      try {
        await this.fetchStatistics(params);
      } catch (e) {
        console.error('包车统计数据加载失败', e);
      }
      this.loading = false;
      setTimeout(() => this.handleResize(), 300);
    },

    async fetchStatistics(params) {
      const res = await this.$axios.get("/api/v1/manage/order/chartered/statistics", params);
      if (res && res.code === 1 && res.data) {
        const data = res.data;
        this.overview = data;
        this.incomeList = [
          { label: '套餐费收入', value: data.comboRevenue || '0.00', count: data.comboCount || 0, remark: '用户支付的包车套餐费用' },
          { label: '退票手续费', value: data.refundFeeRevenue || '0.00', count: data.refundCount || 0, remark: '退票时收取的手续费' },
          { label: '超时/超里程费', value: data.beyondFeeRevenue || '0.00', count: data.beyondCount || 0, remark: '超出套餐包含时间或里程的费用' },
          { label: '司机提成支出', value: data.driverCommission || '0.00', count: '-', remark: '支付给司机的提成费用' },
          { label: '平台净收入', value: data.platformIncome || '0.00', count: '-', remark: '总收入 - 司机提成' },
        ];
        this.driverData = (data.driverRank || []).map(d => ({
          driverName: d.driverName, driverPhone: d.driverPhone,
          orderCount: d.orderCount, finishCount: d.finishCount,
          totalRevenue: d.totalRevenue, completionRate: d.completionRate
        }));
        this.vehicleData = (data.vehicleRank || []).map(v => ({
          vehiclePlate: v.vehiclePlate, driverName: v.driverName,
          orderCount: v.orderCount, totalRevenue: v.totalRevenue, avgOrderFee: v.avgOrderFee
        }));
        this.$nextTick(() => {
          this.renderStatusChart(data.statusDistribution || []);
          this.renderComboChart(data.comboDistribution || []);
          this.renderTrendChart(data.dailyTrend || []);
          this.renderRevenueTrendChart(data.dailyTrend || []);
          this.renderCityChart(data.cityTop10 || []);
        });
      }
    },

    initChart(refName) {
      if (this.charts[refName]) this.charts[refName].dispose();
      const dom = this.$refs[refName];
      if (!dom) return null;
      const chart = echarts.init(dom);
      this.charts[refName] = chart;
      return chart;
    },

    renderStatusChart(data) {
      const chart = this.initChart('statusChart');
      if (!chart) return;
      const colors = ['#409EFF', '#67C23A', '#F56C6C', '#E6A23C', '#909399'];
      chart.setOption({
        tooltip: { trigger: 'item', formatter: '{b}: {c}单 ({d}%)' },
        legend: { bottom: 5, type: 'scroll', textStyle: { fontSize: 11 } },
        color: colors,
        series: [{ type: 'pie', radius: ['35%', '58%'], center: ['50%', '45%'],
          avoidLabelOverlap: true, label: { show: false },
          emphasis: { label: { show: true, fontSize: 12, fontWeight: 'bold' } },
          data: data.map(d => ({ name: d.name, value: d.count })).filter(d => d.value > 0)
        }]
      });
    },

    renderComboChart(data) {
      const chart = this.initChart('comboChart');
      if (!chart) return;
      chart.setOption({
        tooltip: { trigger: 'item', formatter: '{b}: {c}单 ({d}%)' },
        legend: { bottom: 5, type: 'scroll', textStyle: { fontSize: 11 } },
        series: [{ type: 'pie', radius: ['35%', '58%'], center: ['50%', '45%'],
          avoidLabelOverlap: true, label: { show: false },
          emphasis: { label: { show: true, fontSize: 12, fontWeight: 'bold' } },
          data: data.map(d => ({ name: d.name, value: d.count })).filter(d => d.value > 0)
        }]
      });
    },

    renderTrendChart(data) {
      const chart = this.initChart('trendChart');
      if (!chart) return;
      chart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { data: ['总订单', '完成', '退票'], top: 5 },
        xAxis: { type: 'category', data: data.map(d => d.date), axisLabel: { rotate: 30, fontSize: 11 } },
        yAxis: { type: 'value', minInterval: 1 },
        series: [
          { name: '总订单', type: 'line', data: data.map(d => d.orderCount), smooth: true, areaStyle: { opacity: 0.15 }, itemStyle: { color: '#409EFF' } },
          { name: '完成', type: 'line', data: data.map(d => d.completedCount), smooth: true, areaStyle: { opacity: 0.15 }, itemStyle: { color: '#67C23A' } },
          { name: '退票', type: 'line', data: data.map(d => d.cancelledCount || 0), smooth: true, areaStyle: { opacity: 0.15 }, itemStyle: { color: '#F56C6C' } },
        ],
        grid: { left: 50, right: 20, bottom: 50, top: 40 }
      });
    },

    renderRevenueTrendChart(data) {
      const chart = this.initChart('revenueTrendChart');
      if (!chart) return;
      chart.setOption({
        tooltip: { trigger: 'axis', formatter: (params) => params.map(p => `${p.seriesName}: ¥${p.value}`).join('<br/>') },
        legend: { data: ['总收入', '平台收入'], top: 5 },
        xAxis: { type: 'category', data: data.map(d => d.date), axisLabel: { rotate: 30, fontSize: 11 } },
        yAxis: { type: 'value' },
        series: [
          { name: '总收入', type: 'bar', data: data.map(d => d.revenue || 0), itemStyle: { color: '#E6A23C' }, barMaxWidth: 30 },
          { name: '平台收入', type: 'bar', data: data.map(d => d.platformIncome || 0), itemStyle: { color: '#67C23A' }, barMaxWidth: 30 },
        ],
        grid: { left: 60, right: 20, bottom: 50, top: 40 }
      });
    },

    renderCityChart(data) {
      const chart = this.initChart('cityChart');
      if (!chart) return;
      const sorted = [...data].sort((a, b) => b.count - a.count).slice(0, 10);
      chart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        xAxis: { type: 'category', data: sorted.map(d => d.city), axisLabel: { rotate: 30, fontSize: 11 } },
        yAxis: { type: 'value', minInterval: 1 },
        series: [{ type: 'bar', data: sorted.map(d => d.count), itemStyle: { color: '#409EFF' }, barMaxWidth: 40 }],
        grid: { left: 50, right: 20, bottom: 60, top: 20 }
      });
    }
  }
};
</script>

<style lang="less" scoped>
#charteredStatistics {
  height: 100%;
  overflow-y: auto;
  padding: 0 0 24px 0;
  background: #f5f7fa;

  .filter-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fff;
    padding: 12px 16px;
    border-radius: 4px;
    margin-bottom: 16px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);

    .filter-left {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
    }
  }

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin: 16px 0 10px;
    padding-left: 10px;
    border-left: 3px solid #409EFF;

    i { margin-right: 6px; color: #409EFF; }
  }

  .overview-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
    margin-bottom: 4px;

    .ov-card {
      background: #fff;
      border-radius: 6px;
      padding: 14px 12px;
      display: flex;
      align-items: center;
      gap: 10px;
      box-shadow: 0 1px 4px rgba(0,0,0,0.06);

      .ov-card-icon {
        width: 42px;
        height: 42px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        i {
          font-size: 20px;
          color: #fff;
        }
      }

      .ov-card-info {
        flex: 1;
        min-width: 0;

        .ov-card-value {
          font-size: 18px;
          font-weight: 700;
          color: #303133;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .ov-card-label {
          font-size: 12px;
          color: #909399;
          margin-top: 2px;
        }
      }
    }
  }

  .charts-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 4px;
  }

  .charts-grid-half {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 4px;
  }

  .chart-card {
    background: #fff;
    border-radius: 6px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    overflow: hidden;

    .chart-card-header {
      padding: 10px 14px;
      border-bottom: 1px solid #f0f0f0;
      font-size: 13px;
      font-weight: 600;
      color: #303133;

      i { margin-right: 4px; color: #409EFF; }
    }

    .chart-body {
      height: 300px;
      width: 100%;
      padding: 8px;
      box-sizing: border-box;
    }
  }

  .table-card {
    background: #fff;
    border-radius: 6px;
    padding: 0 16px 16px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    margin-bottom: 4px;
  }

  .tables-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;

    .table-card {
      margin-bottom: 0;
    }
  }
}
</style>
